import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { QrCode, CheckCircle, XCircle, Camera, CameraOff, ArrowLeft, Users, Ticket, RefreshCw, Volume2, VolumeX } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../utils/api';

export default function TicketCheckinPage() {
  const { eventId } = useParams();
  const { user, token } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraDebug, setCameraDebug] = useState(''); // Debug info for iOS
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [stats, setStats] = useState({ total: 0, checkedIn: 0 });
  const [recentCheckins, setRecentCheckins] = useState([]);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);

  // Audio refs for feedback sounds
  const successSound = useRef(null);
  const errorSound = useRef(null);

  const fetchEventDetails = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}`);
      if (res.ok) {
        const data = await res.json();
        setEvent(data);
      }
    } catch (error) {
      console.error('Error fetching event:', error);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  const fetchCheckinStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/tickets/checkin-stats/${eventId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [eventId, token]);

  // Initialize audio and fetch data on mount
  useEffect(() => {
    // Create audio elements
    successSound.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleHkXZLPx4qx8Myo9p+/z2q14KTZft+/30a5wHD2q7/PTtnoeIFu45O2/mGEpVsXt4LyDXy1Mwe7er3xYNEvB7d2scVUzR8Ht26tsUTRIwu3bqmtQNEnC7dqpaU4zSsPt2qloTTNKw+3aqGhNMkrD7dqoaE0ySsPt2qhoTTJKw+3aqGhNMkrD7dqoaE0ySsPt2qhoTTJKw+3aqGhNMkrD7dqoaA==');
    errorSound.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgoNDXo1wSBz+Z1OGleFQXZK7v3KhwKyo8pe3x1q9vIjZet+vvzapnGz2o6+zNsHEcIF256+S5i1gmVcLq3rF9WCxLvu3bp3NRMkfA7NmnbU0xR8Ht2qZrTDFIwu3ap2pMMUnC7dmmak0xScLt2qdpTDFJwu3apmtMMUnC7dqnak0xScLt2qZrTDFJwu3ap2pMMUnC7dqmak0xScLt2qdqTDFJwu3apmtMMUnC7dqnak0xScLt2qZrTA==');
    
    fetchEventDetails();
    fetchCheckinStats();
    
    // Cleanup function
    return () => {
      // Stop camera tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      // Clear scan interval
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
    };
  }, [fetchEventDetails, fetchCheckinStats]);

  const playSound = (success) => {
    if (!soundEnabled) return;
    try {
      if (success && successSound.current) {
        successSound.current.currentTime = 0;
        successSound.current.play().catch(() => {
          // iOS Safari blocks autoplay - ignore silently
        });
      } else if (!success && errorSound.current) {
        errorSound.current.currentTime = 0;
        errorSound.current.play().catch(() => {
          // iOS Safari blocks autoplay - ignore silently
        });
      }
    } catch (e) {
      // Ignore audio errors
    }
  };

  const startCamera = async () => {
    try {
      // Check if mediaDevices is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Camera not supported on this device/browser.');
        return;
      }

      // Detect iOS
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      
      setCameraDebug(`iOS: ${isIOS}, requesting camera...`);
      
      // Use the simplest possible constraints for maximum compatibility
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' }, 
        audio: false 
      });
      
      setCameraDebug(`Stream obtained, tracks: ${stream.getVideoTracks().length}`);
      
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        const settings = videoTrack.getSettings();
        setCameraDebug(`Track: ${settings.width}x${settings.height}`);
      }
      
      streamRef.current = stream;
      
      // Get the video element
      const video = videoRef.current;
      if (!video) {
        toast.error('Video element not found');
        return;
      }
      
      // CRITICAL FOR iOS: Set these properties BEFORE srcObject
      video.setAttribute('autoplay', '');
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.autoplay = true;
      video.muted = true;
      video.playsInline = true;
      
      // Set srcObject
      video.srcObject = stream;
      
      // iOS Safari sometimes needs this explicit load call
      video.load();
      
      setCameraDebug('srcObject set, waiting for metadata...');
      
      // Wait for the video to be ready
      const playVideo = () => {
        return new Promise((resolve, reject) => {
          // If already playing, resolve immediately
          if (!video.paused && video.readyState >= 2) {
            setCameraDebug('Already playing');
            resolve();
            return;
          }
          
          const onCanPlay = () => {
            video.removeEventListener('canplay', onCanPlay);
            setCameraDebug(`canplay fired, trying play()... readyState=${video.readyState}`);
            
            video.play()
              .then(() => {
                setCameraDebug(`Playing! ${video.videoWidth}x${video.videoHeight}`);
                resolve();
              })
              .catch(e => {
                setCameraDebug(`play() error: ${e.message}`);
                reject(e);
              });
          };
          
          video.addEventListener('canplay', onCanPlay);
          
          // Fallback timeout
          setTimeout(() => {
            video.removeEventListener('canplay', onCanPlay);
            if (video.readyState >= 2) {
              video.play()
                .then(() => {
                  setCameraDebug(`Timeout play worked! ${video.videoWidth}x${video.videoHeight}`);
                  resolve();
                })
                .catch(reject);
            } else {
              setCameraDebug(`Timeout: readyState=${video.readyState}, paused=${video.paused}`);
              // Try to force play anyway
              video.play().then(resolve).catch(reject);
            }
          }, 3000);
        });
      };
      
      await playVideo();
      
      // Verify the video is actually displaying
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        setCameraDebug('WARNING: Video dimensions are 0!');
      }
      
      setCameraActive(true);
      startScanning();
      toast.success('Camera started');
      
    } catch (error) {
      console.error('Camera error:', error);
      setCameraDebug(`Error: ${error.name} - ${error.message}`);
      
      if (error.name === 'NotAllowedError') {
        toast.error('Camera access denied. Please allow camera permissions.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No camera found on this device.');
      } else if (error.name === 'NotReadableError') {
        toast.error('Camera is in use by another app.');
      } else {
        toast.error(`Camera error: ${error.message}`);
      }
    }
  };

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    setCameraActive(false);
    setScanning(false);
  }, []);

  const startScanning = () => {
    if (scanIntervalRef.current) return;
    
    setScanning(true);
    
    // Import QR scanner library dynamically
    const loadScanner = async () => {
      // Using jsQR for QR code scanning
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js';
      script.onload = () => {
        scanIntervalRef.current = setInterval(scanFrame, 200);
      };
      document.head.appendChild(script);
    };
    
    if (window.jsQR) {
      scanIntervalRef.current = setInterval(scanFrame, 200);
    } else {
      loadScanner();
    }
  };

  const scanFrame = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || !window.jsQR) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    if (video.readyState !== video.HAVE_ENOUGH_DATA) return;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = window.jsQR(imageData.data, imageData.width, imageData.height);
    
    if (code && code.data) {
      // Extract ticket ID from QR data
      // Expected format: https://linedancecentral.uk/tickets/validate/{ticket_id}
      const match = code.data.match(/validate\/([a-zA-Z0-9_-]+)/);
      if (match) {
        const ticketId = match[1];
        processTicket(ticketId);
      }
    }
  }, []);

  const processTicket = async (ticketId) => {
    // Prevent duplicate scans
    if (scanResult?.ticket_id === ticketId && Date.now() - (scanResult.timestamp || 0) < 3000) {
      return;
    }
    
    setScanning(false);
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }

    try {
      // First validate the ticket
      const validateRes = await fetch(`${API_URL}/api/tickets/validate/${ticketId}`);
      const validateData = await validateRes.json();
      
      if (!validateData.valid) {
        setScanResult({
          success: false,
          message: validateData.message,
          ticket: validateData.ticket,
          ticket_id: ticketId,
          timestamp: Date.now()
        });
        playSound(false);
        
        // Auto-resume scanning after 2 seconds
        setTimeout(() => {
          setScanResult(null);
          startScanning();
        }, 2000);
        return;
      }
      
      // Check if ticket is for this event
      if (validateData.ticket?.event_id !== eventId) {
        setScanResult({
          success: false,
          message: `Ticket is for a different event (${validateData.ticket?.event_id} vs ${eventId})`,
          ticket: validateData.ticket,
          ticket_id: ticketId,
          timestamp: Date.now()
        });
        playSound(false);
        
        setTimeout(() => {
          setScanResult(null);
          startScanning();
        }, 2000);
        return;
      }
      
      // Check in the ticket
      const checkinRes = await fetch(`${API_URL}/api/tickets/checkin/${ticketId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      
      if (checkinRes.ok) {
        const checkinData = await checkinRes.json();
        setScanResult({
          success: true,
          message: 'Checked in successfully!',
          ticket: checkinData.ticket,
          ticket_id: ticketId,
          timestamp: Date.now()
        });
        playSound(true);
        
        // Update stats
        setStats(prev => ({ ...prev, checkedIn: prev.checkedIn + 1 }));
        
        // Add to recent checkins
        setRecentCheckins(prev => [{
          ...checkinData.ticket,
          checked_in_at: new Date().toISOString()
        }, ...prev.slice(0, 9)]);
        
        // Auto-resume scanning after 2 seconds
        setTimeout(() => {
          setScanResult(null);
          startScanning();
        }, 2000);
      } else {
        const error = await checkinRes.json();
        setScanResult({
          success: false,
          message: error.detail || 'Check-in failed',
          ticket_id: ticketId,
          timestamp: Date.now()
        });
        playSound(false);
        
        setTimeout(() => {
          setScanResult(null);
          startScanning();
        }, 2000);
      }
    } catch (error) {
      console.error('Check-in error:', error);
      setScanResult({
        success: false,
        message: 'Error processing ticket',
        ticket_id: ticketId,
        timestamp: Date.now()
      });
      playSound(false);
      
      setTimeout(() => {
        setScanResult(null);
        startScanning();
      }, 2000);
    }
  };

  // Manual ticket ID input
  const [manualTicketId, setManualTicketId] = useState('');
  
  const handleManualCheckin = () => {
    if (manualTicketId.trim()) {
      processTicket(manualTicketId.trim());
      setManualTicketId('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={`/events/${eventId}`}>
              <Button variant="ghost" size="sm" className="text-slate-400">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-lg font-bold text-white">Ticket Check-in</h1>
              <p className="text-sm text-slate-400">{event?.title || 'Event'}</p>
            </div>
          </div>
          
          {/* Stats */}
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="flex items-center gap-1 text-emerald-400">
                <Users className="w-4 h-4" />
                <span className="font-bold">{stats.checkedIn}</span>
              </div>
              <p className="text-xs text-slate-400">Checked In</p>
            </div>
            <div className="text-center">
              <div className="flex items-center gap-1 text-slate-300">
                <Ticket className="w-4 h-4" />
                <span className="font-bold">{stats.total}</span>
              </div>
              <p className="text-xs text-slate-400">Total</p>
            </div>
            <button 
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="p-2 text-slate-400 hover:text-white"
              title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Scanner Section */}
          <div className="space-y-4">
            {/* Camera View */}
            <div className="relative bg-black rounded-2xl overflow-hidden border border-slate-800" style={{ minHeight: '300px', aspectRatio: '1/1' }}>
              {/* Video element is ALWAYS rendered but hidden when not active */}
              <video 
                ref={videoRef} 
                playsInline
                muted
                autoPlay
                style={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover',
                  background: '#000',
                  display: cameraActive ? 'block' : 'none'
                }}
                data-testid="camera-video"
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              
              {cameraActive ? (
                <>
                  {/* Debug info overlay */}
                  {cameraDebug && (
                    <div className="absolute top-2 left-2 right-2 bg-black/70 text-white text-xs p-2 rounded z-20">
                      {cameraDebug} | Event: {eventId}
                    </div>
                  )}
                  
                  {/* Scan overlay */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-64 h-64 border-2 border-violet-500 rounded-2xl relative">
                      <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-violet-400 rounded-tl-xl" />
                      <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-violet-400 rounded-tr-xl" />
                      <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-violet-400 rounded-bl-xl" />
                      <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-violet-400 rounded-br-xl" />
                      
                      {scanning && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-full h-0.5 bg-violet-500 animate-pulse" />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Scan result overlay */}
                  <AnimatePresence>
                    {scanResult && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className={`absolute inset-0 flex items-center justify-center ${
                          scanResult.success ? 'bg-emerald-500/90' : 'bg-red-500/90'
                        }`}
                      >
                        <div className="text-center p-6">
                          {scanResult.success ? (
                            <CheckCircle className="w-20 h-20 text-white mx-auto mb-4" />
                          ) : (
                            <XCircle className="w-20 h-20 text-white mx-auto mb-4" />
                          )}
                          <p className="text-2xl font-bold text-white mb-2">
                            {scanResult.success ? 'CHECKED IN' : 'INVALID'}
                          </p>
                          <p className="text-white/80">{scanResult.message}</p>
                          {scanResult.ticket?.attendee_name && (
                            <p className="text-white text-xl font-semibold mt-2">
                              {scanResult.ticket.attendee_name}
                            </p>
                          )}
                          {scanResult.ticket?.ticket_type_name && (
                            <Badge className="mt-2 bg-white/20 text-white">
                              {scanResult.ticket.ticket_type_name}
                            </Badge>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center p-8">
                  <CameraOff className="w-16 h-16 text-slate-700 mb-4" />
                  <p className="text-slate-400 text-center mb-4">Camera not active</p>
                  <Button
                    onClick={startCamera}
                    className="bg-violet-600 hover:bg-violet-500"
                    data-testid="start-camera-btn"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Start Camera
                  </Button>
                </div>
              )}
            </div>

            {/* Camera controls */}
            {cameraActive && (
              <div className="flex gap-2">
                <Button
                  onClick={stopCamera}
                  variant="outline"
                  className="flex-1 border-slate-700 text-slate-300"
                >
                  <CameraOff className="w-4 h-4 mr-2" />
                  Stop Camera
                </Button>
                <Button
                  onClick={() => {
                    if (scanning) {
                      if (scanIntervalRef.current) {
                        clearInterval(scanIntervalRef.current);
                        scanIntervalRef.current = null;
                      }
                      setScanning(false);
                    } else {
                      startScanning();
                    }
                  }}
                  className={scanning ? 'bg-amber-600 hover:bg-amber-500' : 'bg-emerald-600 hover:bg-emerald-500'}
                >
                  {scanning ? 'Pause Scan' : 'Resume Scan'}
                </Button>
              </div>
            )}

            {/* Manual input */}
            <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
              <h3 className="text-sm font-medium text-slate-400 mb-3">Manual Entry</h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={manualTicketId}
                  onChange={(e) => setManualTicketId(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleManualCheckin()}
                  placeholder="Enter ticket ID or code"
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-500"
                  data-testid="manual-ticket-input"
                />
                <Button
                  onClick={handleManualCheckin}
                  disabled={!manualTicketId.trim()}
                  className="bg-violet-600 hover:bg-violet-500"
                  data-testid="manual-checkin-btn"
                >
                  Check In
                </Button>
              </div>
            </div>
          </div>

          {/* Recent Check-ins */}
          <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
            <div className="px-4 py-3 bg-slate-800/50 border-b border-slate-800 flex items-center justify-between">
              <h3 className="font-semibold text-white">Recent Check-ins</h3>
              <button 
                onClick={fetchCheckinStats}
                className="text-slate-400 hover:text-white"
                title="Refresh"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-[500px] overflow-y-auto">
              {recentCheckins.length === 0 ? (
                <div className="p-8 text-center">
                  <Ticket className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                  <p className="text-slate-400">No check-ins yet</p>
                  <p className="text-slate-600 text-sm">Scanned tickets will appear here</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-800">
                  {recentCheckins.map((checkin, idx) => (
                    <div key={checkin.ticket_id || idx} className="px-4 py-3 hover:bg-slate-800/50">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-white">{checkin.attendee_name}</p>
                          <p className="text-sm text-violet-400">{checkin.ticket_type_name}</p>
                        </div>
                        <div className="text-right">
                          <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Checked In
                          </Badge>
                          <p className="text-xs text-slate-400 mt-1">
                            {checkin.checked_in_at ? new Date(checkin.checked_in_at).toLocaleTimeString() : ''}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
